import { m8API } from 'services/production/m8';
import { TGetClientDetailsParams } from 'types/services';
import { TClientDetails, TClientDetailsPaymentsInfo } from 'types/state';
import { normalizeM8UserFullInfo } from 'services/production/utils/normalizeM8UserFullInfo';
import { HTTPResponseError } from 'utils/errors';
import { M8BillRow } from 'services/production/types';
import { normalizeM8PartnerDTO } from 'services/production/utils/normalizeM8PartnerDTO';
import { normalizeM8ProductDTO } from 'services/production/utils/normalizeM8ProductDTO';
import { normalizeM8DemoStackDTO } from 'services/production/utils/normalizeM8DemoStackDTO';
import { normalizeM8PaymentTransactionDTO } from 'services/production/utils/normalizeM8PaymentTransactionDTO';
import { normalizeM8ClientBalance } from '../utils/normalizeM8ClientBalance';

export const getClientDetails = async (
  params: TGetClientDetailsParams
): Promise<TClientDetails> => {
  const { clientId } = params;

  // TS < 4.5 не поддерживает Promise.all с количеством элементов больше 10, поэтому разбиваем на части
  // TODO: убрать когда проект перейдет на TS > 4.5
  const promisedM8BillRows = Promise.all([
    m8API.admin.getBillRows({
      idUser: clientId,
      year: new Date().getFullYear(),
    }),
    m8API.admin.getBillRows({
      idUser: clientId,
      year: new Date().getFullYear() - 1,
    }),
  ]);

  const [
    user,
    m8ProdStackDTO,
    m8DemoStackDTO,
    userTransactions,
    balance,
    legalRequesites,
    m8BillRowsPack,
    userStackActivity,
    m8ProductDTOs,
    m8ClientBalance,
  ] = await Promise.all([
    m8API.admin.getUser(clientId),
    m8API.admin.getUserProdStack({ idUser: clientId }),
    m8API.admin.getUserDemoStack({ idUser: clientId }),
    m8API.admin.getPaymentTransactionsByUser(clientId),
    m8API.admin.getAccountBalance({ idUser: clientId }),
    m8API.admin.getUserLegalRequisites({ idUser: clientId }),
    promisedM8BillRows,
    m8API.admin.getUserStackActivity({ idUser: clientId }).catch((error) => {
      if (error instanceof HTTPResponseError) {
        if (error.status === 500) {
          return -1;
        }
      }

      throw error;
    }),
    m8API.admin.getProducts({ idUser: clientId }),
    m8API.admin.getClientBalance({ idClient: clientId }),
  ]);

  const partner = user.partner
    ? normalizeM8PartnerDTO(user.partner).toPartner()
    : null;

  const paymentsInfo: TClientDetailsPaymentsInfo = {
    balance,
    transactions: userTransactions.map((transaction) =>
      normalizeM8PaymentTransactionDTO(transaction).toTransactionPayment()
    ),
  };

  const [
    M8BillRowsByMonthThisYear,
    M8BillRowsByMonthPreviousYear,
  ] = m8BillRowsPack;

  const m8BillRows = [
    ...Object.values(M8BillRowsByMonthThisYear),
    ...Object.values(M8BillRowsByMonthPreviousYear),
  ]
    .reduce<M8BillRow[]>((acc, rows) => {
      return [...acc, ...rows];
    }, [])
    .sort((a, b) => {
      return (
        new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
      );
    });

  const stackInfo = {
    demoStackInfo: m8DemoStackDTO
      ? normalizeM8DemoStackDTO(m8DemoStackDTO).toDemoStackInfo()
      : null,
    prodStackInfo:
      user.prodUrl !== null
        ? normalizeM8UserFullInfo(user).toProdStackInfo(m8ProdStackDTO)
        : null,
    managersCount: userStackActivity,
  };

  const clientDetails: TClientDetails = {
    clientInfo: normalizeM8UserFullInfo(user).toClientDetailsClientInfo(),
    stackInfo,
    userProfileInfo: normalizeM8UserFullInfo(user).toUserProfileInfo(),
    paymentsInfo,
    legalRequesites,
    m8BillRows,
    partner,
    clientProducts: m8ProductDTOs.map((m8ProductDTO) =>
      normalizeM8ProductDTO(m8ProductDTO).toClientProduct()
    ),
    finances: normalizeM8ClientBalance(m8ClientBalance).toClientFinances(),
  };

  return clientDetails;
};
